import { usePostInstitutionApi, usePutInstitutionApi } from 'api/institution'
import { Flex } from 'components/shared/flex'
import { CheckboxField, ChoiceField, DateField, InputField, MultipleSelectField, SelectField } from 'components/shared/input'
import { GlobalStateContext } from 'contexts/global_state_context'
import { useContext, useEffect, useState } from 'react'
import { Button, CloseButton, Modal, Table } from 'react-bootstrap'
import { Form, useEffectSkipFirst, useForm } from 'utils/hooks'
import { ApiError } from 'utils/network/api_hooks'
import { Institution, InstitutionForm } from 'entities/institution'
import { AddressFormView } from 'components/address/address_form'
import { useFetchOrganizationsApi } from 'api/organization'
import { useFetchProductItemsApi } from 'api/product_item'
import { ThemeContext } from 'contexts/theme_context'
import { useFetchSalesAgenciesApi } from 'api/sales_agency'
import { useFetchProductSetsApi } from 'api/product_set'
import _ from 'lodash'
import { Organization } from 'entities/organization'
import { SalesAgency } from 'entities/sales_agency'
import { ProductSet } from 'entities/product_set'
import { ProductItem } from 'entities/product_item'
import { useFetchDiscountsApi } from 'api/discount'
import { Discount } from 'entities/discount'
import { useFetchInstitutionPriceVersionsApi } from 'api/institution_price_version'
import { InstitutionPriceVersion } from 'entities/institution_price_version'
import { ShipmentTaskItemTemplate } from 'entities/shipment_task_item_template'
import { useFetchShipmentTaskItemTemplatesApi } from 'api/shipment_task_item_template'

export type FormViewProps = {
    form: Form<InstitutionForm>
    apiError: ApiError
    isEdit?: boolean
    organizations: Organization[]
    priceVersions: InstitutionPriceVersion[]
    salesAgencies: SalesAgency[]
    productSets: ProductSet[]
    productItems: ProductItem[]
    discounts: Discount[]
    shipmentTaskItemTemplates: ShipmentTaskItemTemplate[]
}

const FormView = ({
    form,
    apiError,
    isEdit,
    organizations,
    priceVersions,
    salesAgencies,
    productItems,
    productSets,
    discounts,
    shipmentTaskItemTemplates,
}: FormViewProps) => {
    const theme = useContext(ThemeContext)
    const isHomeTest = !!process.env.REACT_APP_IS_HOME_TEST
    const [currentPriceType, setCurrentPriceType] = useState('new')

    useEffectSkipFirst(() => {
        if (form.object.productSetIds && form.object.productSetIds.length > 0) {
            try {
                form.update((f) => {
                    const uniqProductItemIds = _.uniq(
                        form.object.productSetIds!.map((id) => productSets.find((ps) => ps.id === id)!.productItems!.map((pi) => pi.id)).flat()
                    ) as number[]
                    f.productItemIds = uniqProductItemIds
                })
            } catch (e) {
                // pass
            }
        }
    }, [form.object.productSetIds])

    useEffectSkipFirst(() => {
        form.update((f) => {
            f.discountInstitutions = form.object.discountIds?.map((id) => {
                const discount = form.object.discountInstitutions?.find((d) => d.discountId === id)
                if (discount) {
                    return discount
                } else {
                    return { discountId: id }
                }
            })
        })
    }, [form.object.discountIds])

    useEffectSkipFirst(() => {
        console.log(form.object)
    }, [form.object])

    // institutionType選択時にgraceDaysとpublicationStartDateの値も入れる
    useEffect(() => {
        if (isEdit) {
            return
        }
        if (form.object.institutionType === 'HEALTH_CHECKUP_CENTER') {
            form.update((f) => {
                f.graceDays = 9
                f.publicationStartDate = undefined
            })
        } else {
            form.update((f) => {
                f.graceDays = 14
                const today = new Date().toISOString().split('T')[0]
                f.publicationStartDate = today
            })
        }
    }, [form.object.institutionType, isEdit])

    // 単がん種の値段設定を行う
    const togglePrice = () => {
        if (currentPriceType === 'old') {
            const newProductItemInfos = productItems.map((productItem) => {
                return {
                    productItemId: productItem.id,
                    priceWithoutTax: 44000,
                }
            })

            form.update((f) => {
                f.productItemInfos = newProductItemInfos
            })
            setCurrentPriceType('new')
        } else {
            const oldProductItemInfos = productItems.map((productItem) => {
                return {
                    productItemId: productItem.id,
                    priceWithoutTax: 42000,
                }
            })

            form.update((f) => {
                f.productItemInfos = oldProductItemInfos
            })
            setCurrentPriceType('old')
        }
    }

    // 製品セットの表示の優先順位を取得する
    const getPriority = (name: string) => {
        if (name.includes('LSI')) return 4
        if (name.includes('日本臨床') || name.includes('名鉄')) return 5
        if (name.includes('【新価格】')) return 0
        if (name.includes('【海外用】') && name.includes('10がん種セット')) return 1
        if (name.includes('7がん種') || name.includes('3大がん') || name.includes('消化器がん') || name.includes('女性がん')) return 2
        if (name.includes('【海外用】') && name.includes('7がん種セット')) return 3
        return 6
    }
    // 製品セットの表示優先順位でソート
    const sortedProductSets = [...productSets].sort((a, b) => {
        return getPriority(a.name ?? '') - getPriority(b.name ?? '')
    })

    return (
        <>
            <SelectField
                form={form}
                attr="organizationId"
                apiError={apiError}
                label="組織グループ"
                includeBlank
                selectItems={organizations.map((o) => {
                    return { label: o.name, value: o.id! }
                })}
            />
            <Flex>
                <InputField form={form} attr="name" apiError={apiError} label="名称" required />
            </Flex>
            <Flex>
                <SelectField
                    form={form}
                    attr={'institutionType'}
                    apiError={apiError}
                    label="種類"
                    selectItems={[
                        { label: 'クリニック', value: 'CLINIC' },
                        { label: '健診センター', value: 'HEALTH_CHECKUP_CENTER' },
                    ]}
                    required
                />
            </Flex>
            <Flex>
                <InputField form={form} attr="personInCharge" apiError={apiError} label="担当者名" required />
            </Flex>
            <Flex>
                <SelectField
                    form={form}
                    attr="salesAgencyId"
                    apiError={apiError}
                    label="営業代行"
                    selectItems={salesAgencies.map((salesAgency) => {
                        return { label: salesAgency.name, value: salesAgency.id! }
                    })}
                    includeBlank
                />
            </Flex>
            <Flex>
                <DateField form={form} attr="contractDate" apiError={apiError} label="契約日" required />
                <ChoiceField form={form} attr="shippingType" apiError={apiError} label="発送パターン" tableName="institution" required />
            </Flex>
            <Flex>
                <InputField form={form} attr="graceDays" apiError={apiError} label="結果発送猶予日数" placeholder="デフォルト14日" type="number" min={1} />
                <SelectField
                    form={form}
                    attr={'resultPackingType'}
                    apiError={apiError}
                    label="結果返送パターン"
                    selectItems={[
                        { label: '冊子', value: '冊子' },
                        { label: '個別', value: '個別' },
                    ]}
                    blankLabel="冊子"
                    style={{ width: 378 }}
                />
            </Flex>
            <Flex>
                <InputField
                    form={form}
                    attr="scanSendFormCount"
                    apiError={apiError}
                    label="依頼書発送部数（SCAN）"
                    placeholder="デフォルト10部"
                    type="number"
                    min={1}
                />
                <InputField
                    form={form}
                    attr="checkSendFormCount"
                    apiError={apiError}
                    label="依頼書発送部数（CHECK）"
                    placeholder="デフォルト10部"
                    type="number"
                    min={1}
                />
                <InputField
                    form={form}
                    attr="liteSendFormCount"
                    apiError={apiError}
                    label="依頼書発送部数（LITE）"
                    placeholder="デフォルト10部"
                    type="number"
                    min={1}
                />
            </Flex>
            <Flex>
                <CheckboxField
                    form={form}
                    attr="isAutoChargeRequestFormOnSave"
                    checkboxLabel={`${
                        isEdit ? '（更新ボタン押下時）依頼書残数50%以下の場合に資材発送タスクを作成する' : '作成時に依頼書発送部数の資材発送タスクを作成する'
                    }`}
                    style={{ paddingTop: '2px', paddingBottom: '8px' }}
                />
            </Flex>
            <div>イレギュラー資材発送テンプレート指定</div>
            <Flex>
                <SelectField
                    form={form}
                    attr="newShipmentTaskItemTemplateId"
                    apiError={apiError}
                    label="初回テンプレート"
                    includeBlank
                    selectItems={shipmentTaskItemTemplates.map((template) => ({
                        label: template.name,
                        value: template.id,
                    }))}
                />
                <SelectField
                    form={form}
                    attr="addShipmentTaskItemTemplateId"
                    apiError={apiError}
                    label="追加テンプレート"
                    includeBlank
                    selectItems={shipmentTaskItemTemplates.map((template) => ({
                        label: template.name,
                        value: template.id,
                    }))}
                />
            </Flex>
            <Flex>
                <SelectField
                    form={form}
                    attr="institutionPriceVersionId"
                    apiError={apiError}
                    label="価格バージョン"
                    includeBlank
                    selectItems={priceVersions.map((p) => {
                        return { label: p.name, value: p.id! }
                    })}
                />
            </Flex>
            <InputField form={form} attr="url" apiError={apiError} label="URL" placeholder="https://~~~" />
            <Flex>
                <InputField form={form} attr="email" apiError={apiError} label="メールアドレス" />
                <InputField form={form} attr="tel" apiError={apiError} label="電話番号(ハイフンあり)" placeholder="xx-xxxx-xxxx" />
            </Flex>
            <CheckboxField
                form={form}
                attr="isSystemEmailPermission"
                checkboxLabel="システムからメールを送付する"
                style={{ paddingTop: '2px', paddingBottom: '8px' }}
            />
            <div style={{ marginTop: 20, marginBottom: 20 }}>
                <div>請求書送付先メールアドレスの管理</div>
                <Flex>
                    <InputField form={form} attr="invoiceSendEmail" apiError={apiError} label="宛先（To）" />
                    <InputField form={form} attr="invoiceSendEmailCc" apiError={apiError} label="Cc" />
                </Flex>
            </div>

            <div style={{ marginTop: 20 }}>Webサイトへの掲載管理</div>
            <Flex style={{ marginTop: 12 }}>
                <DateField form={form} attr="publicationStartDate" label="掲載開始日" />
                <DateField form={form} attr="terminationDate" label="契約終了日" />
            </Flex>

            {!isHomeTest && (
                <>
                    <Flex>
                        <MultipleSelectField
                            hideSearch
                            id="institution_discount_form"
                            form={form}
                            attr="discountIds"
                            apiError={apiError}
                            label="割引"
                            selectItems={discounts.map((discount) => ({
                                label: discount.name,
                                value: discount.id!,
                            }))}
                        />
                    </Flex>
                    <div>
                        <Table>
                            <thead>
                                <tr>
                                    <th>適用するディスカウント</th>
                                    <th style={{ width: 150, minWidth: 150 }}>開始日</th>
                                    <th style={{ width: 150, minWidth: 150 }}>終了日</th>
                                </tr>
                            </thead>
                            <tbody>
                                {form.object.discountIds
                                    ?.map((discountId) => discounts.find((d) => d.id === discountId)!)
                                    .map((discount, index) => {
                                        return (
                                            <tr key={'discount-' + discount.id}>
                                                <td>{discount.name}</td>
                                                <td>
                                                    <DateField
                                                        form={form}
                                                        attr={['discountInstitutions', index, 'expiryStartDate']}
                                                        disabled={
                                                            !(
                                                                form.object.discountInstitutions &&
                                                                form.object.discountInstitutions[index]?.discountId === discount.id
                                                            )
                                                        }
                                                    />
                                                </td>
                                                <td>
                                                    <DateField
                                                        form={form}
                                                        attr={['discountInstitutions', index, 'expiryEndDate']}
                                                        disabled={
                                                            !(
                                                                form.object.discountInstitutions &&
                                                                form.object.discountInstitutions[index]?.discountId === discount.id
                                                            )
                                                        }
                                                    />
                                                </td>
                                            </tr>
                                        )
                                    })}
                            </tbody>
                        </Table>
                    </div>
                    <CheckboxField
                        form={form}
                        attr="isForeignCountry"
                        label="海外キット販売"
                        checkboxLabel="海外キットの販売をする"
                        style={{ paddingTop: '8px', paddingBottom: '8px' }}
                    />
                    <CheckboxField
                        form={form}
                        attr="isAutoShipping"
                        label="キット自動発送"
                        checkboxLabel="キットを自動発送する"
                        hint={
                            <>
                                デフォルトで自動発送になり、検査キットが少なくなった時、施設の依頼の有無に関わらずキットを発送します。
                                <br />
                                施設から追加発送依頼があった時においてのみ発送する場合は、こちらのチェックを外してください。
                                <br />
                                （チェックを外した場合、ダッシュボードで資材残り割合の集計から除外します。）
                            </>
                        }
                        style={{ paddingTop: '8px', paddingBottom: '8px' }}
                    />
                    <Flex>
                        <MultipleSelectField
                            hideSearch
                            id="institution_product_set_form"
                            form={form}
                            attr="productSetIds"
                            apiError={apiError}
                            label="製品セット"
                            selectItems={sortedProductSets.map((ps) => {
                                const hasGenderInName =
                                    ps.name?.endsWith('女性)') || ps.name?.endsWith('男性)') || ps.name?.endsWith('女性）') || ps.name?.endsWith('男性）')

                                return {
                                    label: `${ps.name}${!hasGenderInName && ps.gender ? '(' + { MALE: '男性', FEMALE: '女性' }[ps.gender] + ')' : ''} ${
                                        ps.note ?? ''
                                    }`,
                                    value: ps.id!,
                                }
                            })}
                        />
                    </Flex>
                    <div style={{ marginTop: 20, marginBottom: 20 }}>
                        <div>単がん種</div>
                        <div style={{ marginTop: 20, marginBottom: 20 }}>
                            <Button onClick={togglePrice}>{currentPriceType === 'old' ? '新価格設定にする' : '旧価格設定にする'}</Button>
                        </div>
                        {productItems.map((pi, index) => {
                            const getIndex = () => {
                                return form.object.productItemInfos?.findIndex((pii) => pii.productItemId === pi.id) ?? -1
                            }
                            return (
                                <Flex key={`institution_form_${form.object.id}_${pi.id}`} style={{ marginRight: 20 }} alignItems="center">
                                    <div style={{ width: 200 }}>
                                        <CheckboxField
                                            form={form}
                                            key={'product_item_checkbox' + pi.id}
                                            attr={['productItemInfos', getIndex(), 'productItemId']}
                                            checkboxLabel={pi.name!}
                                            value={pi.id}
                                            onChange={(e) => {
                                                if (e?.target.checked) {
                                                    form.update((f) => {
                                                        f.productItemInfos?.push({ productItemId: pi.id })
                                                    })
                                                } else {
                                                    form.update((f) => {
                                                        f.productItemInfos = f.productItemInfos?.filter((pii) => pii.productItemId !== pi.id)
                                                    })
                                                }
                                            }}
                                        />
                                    </div>
                                    <InputField
                                        key={'product_item_price_' + pi.id}
                                        form={form}
                                        attr={['productItemInfos', getIndex(), 'priceWithoutTax']}
                                        apiError={apiError}
                                        placeholder="デフォルト42,000円(税抜)"
                                        type="number"
                                        min={0}
                                        style={{ width: 250 }}
                                        disabled={!form.object.productItemInfos?.find((pii) => pii.productItemId === pi.id)}
                                    />
                                </Flex>
                            )
                        })}
                    </div>
                    <div>
                        <Flex>
                            <InputField form={form} attr="misignalCheckPrice" apiError={apiError} label="チェック価格（税抜）" type="number" min={0} />
                            <InputField form={form} attr="misignalLitePrice" apiError={apiError} label="ライト価格（税抜）" type="number" min={0} />
                        </Flex>
                    </div>
                </>
            )}

            {!isEdit && (
                <div style={{ padding: '20px 0' }}>
                    <div>医者情報</div>

                    {form.object.doctors?.map((doctor, index) => {
                        return (
                            <Flex key={'new-doctor-' + index} alignItems="center">
                                <Flex>
                                    <InputField
                                        form={form}
                                        attr={['doctors', index, 'familyName']}
                                        apiError={apiError}
                                        label="姓"
                                        required
                                        placeholder="例）山田"
                                    />
                                    <InputField
                                        form={form}
                                        attr={['doctors', index, 'givenName']}
                                        apiError={apiError}
                                        label="名"
                                        required
                                        placeholder="例）太郎"
                                    />
                                </Flex>
                                <Flex>
                                    <InputField
                                        form={form}
                                        attr={['doctors', index, 'familyNameKana']}
                                        apiError={apiError}
                                        label="せい"
                                        placeholder="例）やまだ"
                                    />
                                    <InputField
                                        form={form}
                                        attr={['doctors', index, 'givenNameKana']}
                                        apiError={apiError}
                                        label="めい"
                                        placeholder="例）たろう"
                                    />
                                </Flex>
                                <CloseButton
                                    style={{ marginTop: 22 }}
                                    onClick={() => {
                                        form.update((f) => {
                                            f.doctors?.splice(index, 1)
                                        })
                                    }}
                                ></CloseButton>
                            </Flex>
                        )
                    })}

                    <Button
                        variant="secondary"
                        onClick={() => {
                            form.update((f) => {
                                f.doctors?.push({})
                            })
                        }}
                    >
                        + 医者追加
                    </Button>
                </div>
            )}
            {/* 通常の住所フォーム */}
            <div style={{ marginTop: 20, marginBottom: 20 }}>
                <div>住所情報</div>
                <AddressFormView form={form} apiError={apiError} />
            </div>
            
            {/* 請求先住所フォーム */}
            <div style={{ marginTop: 20, marginBottom: 20 }}>
                <div>請求先住所</div>
                {form.object.invoiceAddress ? (
                    <div style={{ position: 'relative', padding: '10px', border: '1px solid #ddd', borderRadius: '5px', marginBottom: '10px' }}>
                        <CloseButton
                            style={{ position: 'absolute', top: 10, right: 10 }}
                            onClick={() => {
                                form.update((f) => {
                                    f.invoiceAddress = undefined
                                })
                            }}
                        />
                        <AddressFormView 
                            form={form} 
                            apiError={apiError} 
                            prefix="invoiceAddress" 
                        />
                    </div>
                ) : (
                    <Button
                        variant="secondary"
                        onClick={() => {
                            form.update((f) => {
                                f.invoiceAddress = {}
                            })
                        }}
                    >
                        + 請求先住所追加
                    </Button>
                )}
            </div>
            
            {/* Web掲載用住所フォーム */}
            <div style={{ marginTop: 20, marginBottom: 20 }}>
                <div>Web掲載用住所</div>
                {form.object.webAddress ? (
                    <div style={{ position: 'relative', padding: '10px', border: '1px solid #ddd', borderRadius: '5px', marginBottom: '10px' }}>
                        <CloseButton
                            style={{ position: 'absolute', top: 10, right: 10 }}
                            onClick={() => {
                                form.update((f) => {
                                    f.webAddress = undefined
                                })
                            }}
                        />
                        <AddressFormView 
                            form={form} 
                            apiError={apiError} 
                            prefix="webAddress" 
                        />
                    </div>
                ) : (
                    <Button
                        variant="secondary"
                        onClick={() => {
                            form.update((f) => {
                                f.webAddress = {}
                            })
                        }}
                    >
                        + Web掲載用住所追加
                    </Button>
                )}
            </div>
        </>
    )
}

type NewInstitutionFormProps = {
    onComplete?: () => void
}

export const NewInstitutionForm = (props: NewInstitutionFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePostInstitutionApi()
    const form = useForm<InstitutionForm>({
        address: {},
        webAddress: undefined, // 最初は非表示
        invoiceAddress: undefined, // 最初は非表示
        doctors: [{}],
        discountInstitutions: [],
        isAutoShipping: true,
        isAutoChargeRequestFormOnSave: true,
        productItemInfos: [],
        scanSendFormCount: 0,
        checkSendFormCount: 0,
        liteSendFormCount: 0,
        isSystemEmailPermission: true,
        shippingType: 'ゆうパック',
        institutionType: 'CLINIC',
    })
    const [show, setShow] = useState<boolean>(false)
    const isHomeTest = !!process.env.REACT_APP_IS_HOME_TEST
    const organizationsApi = useFetchOrganizationsApi()
    const priceVersionsApi = useFetchInstitutionPriceVersionsApi()
    const salesAgenciesApi = useFetchSalesAgenciesApi()
    const productSetsApi = useFetchProductSetsApi()
    const productItemsApi = useFetchProductItemsApi()
    const discountsApi = useFetchDiscountsApi()
    const shipmentTaskItemTemplatesApi = useFetchShipmentTaskItemTemplatesApi()

    // APIの状態をモニタリング
    useEffect(() => {
        console.log('API状態:', {
            loading: api.loading,
            success: api.isSuccess?.(),
            error: api.apiError,
            response: api.response
        });
    }, [api.loading, api.apiError, api.response]);

    useEffect(() => {
        organizationsApi.execute()
        priceVersionsApi.execute()
        salesAgenciesApi.execute()
        productSetsApi.execute()
        productItemsApi.execute()
        discountsApi.execute()
        shipmentTaskItemTemplatesApi.execute()
    }, [])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                setShow(false)
                props.onComplete()
            }
        }
    }, [api.loading])

    const handleClose = () => {
        setShow(false)
    }

    return (
        <>
            <Button
                variant="success"
                onClick={() => {
                    setShow(true)
                }}
            >
                新規登録
            </Button>
            <Modal show={show} onHide={handleClose} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{isHomeTest ? '提携先' : '病院・クリニック'}の登録</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <>
                        <FormView
                            form={form}
                            apiError={api.apiError}
                            isEdit={false}
                            organizations={organizationsApi.response.results}
                            priceVersions={priceVersionsApi.response.results}
                            salesAgencies={salesAgenciesApi.response.results}
                            productSets={productSetsApi.response.results}
                            productItems={productItemsApi.response.results}
                            discounts={discountsApi.response.results}
                            shipmentTaskItemTemplates={shipmentTaskItemTemplatesApi.response.results}
                        />
                    </>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        閉じる
                    </Button>
                    <Button
                        variant="success" 
                        onClick={(e) => {
                            e.preventDefault();
                            console.log('登録ボタンがクリックされました');
                            handleUpdate();
                        }}
                    >
                        登録
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}
type EditInstitutionFormProps = {
    institution: Institution
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const EditInstitutionForm = (props: EditInstitutionFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePutInstitutionApi()
    const form = useForm<InstitutionForm>({})
    const isHomeTest = !!process.env.REACT_APP_IS_HOME_TEST
    const organizationsApi = useFetchOrganizationsApi()
    const priceVersionsApi = useFetchInstitutionPriceVersionsApi()
    const salesAgenciesApi = useFetchSalesAgenciesApi()
    const productSetsApi = useFetchProductSetsApi()
    const productItemsApi = useFetchProductItemsApi()
    const discountsApi = useFetchDiscountsApi()
    const shipmentTaskItemTemplatesApi = useFetchShipmentTaskItemTemplatesApi()

    useEffect(() => {
        organizationsApi.execute()
        priceVersionsApi.execute()
        salesAgenciesApi.execute()
        productSetsApi.execute()
        productItemsApi.execute()
        discountsApi.execute()
        shipmentTaskItemTemplatesApi.execute()
    }, [])

    useEffectSkipFirst(() => {
        const productItemInfos =
            props.institution.productItemInstitutions?.map((pii) => {
                console.log('pii', pii)
                return { productItemId: pii.productItemId, priceWithoutTax: pii.priceWithoutTax }
            }) ?? []
        console.log('productItemInfos', productItemInfos)
        form.set({
            ...props.institution,
            productItemInfos: productItemInfos,
            isAutoChargeRequestFormOnSave: false,
        })
    }, [props.institution])

    const handleUpdate = () => {        
        api.execute(form);
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        } else {
            console.log('API呼び出し失敗', api.apiError)
        }
    }, [api.loading])

    return (
        <Modal show={props.show} onHide={props.onClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{isHomeTest ? '提携先' : '病院・クリニック'}の編集</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {form.object.id && (
                    <FormView
                        key={form.object.id}
                        form={form}
                        apiError={api.apiError}
                        isEdit
                        organizations={organizationsApi.response.results}
                        priceVersions={priceVersionsApi.response.results}
                        salesAgencies={salesAgenciesApi.response.results}
                        productSets={productSetsApi.response.results}
                        productItems={productItemsApi.response.results}
                        discounts={discountsApi.response.results}
                        shipmentTaskItemTemplates={shipmentTaskItemTemplatesApi.response.results}
                    />
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
